import React from 'react';
import { Check, ImagePlus, Palette, Trash2, X } from 'lucide-react';
import { API } from '../../../lib/api';
import {
  COLORES_TEMA, FUENTES, PRESETS_CABECERA, TAMANOS, TEMA_DEFECTO, fondoCabecera, fondosDe,
} from '../../../lib/formularios';

const ESTILOS = [
  ['encabezado', 'Encabezado'],
  ['pregunta', 'Pregunta'],
  ['texto', 'Texto'],
];

const normalizarHex = (c) => String(c || '').toLowerCase();

/**
 * Panel "Personalizar tema": fuentes, imagen de cabecera, color y fondo. Se abre encima del lienzo
 * (a la derecha en escritorio, desde abajo en el teléfono) para ver el cambio mientras se elige.
 */
export default function PanelTema({ tema: temaActual, onCambiar, onCerrar, subirImagen, deshabilitado }) {
  const tema = {
    ...TEMA_DEFECTO,
    ...(temaActual || {}),
    fuentes: { ...TEMA_DEFECTO.fuentes, ...(temaActual?.fuentes || {}) },
  };
  const cambiar = (campos) => onCambiar({ ...tema, ...campos });

  const cambiarColor = (color) => {
    // Si el fondo era uno de los del color anterior, pasa al equivalente del color nuevo
    const anteriores = fondosDe(tema.color).map(normalizarHex);
    const i = anteriores.indexOf(normalizarHex(tema.fondo));
    cambiar({ color, fondo: i >= 0 ? fondosDe(color)[i] : tema.fondo });
  };

  const cambiarFuente = (clave, campos) => cambiar({
    fuentes: { ...tema.fuentes, [clave]: { ...tema.fuentes[clave], ...campos } },
  });

  const subirCabecera = async () => {
    const img = await subirImagen();
    if (img) cambiar({ imagen: { id: img.id } });
  };

  const personalizado = !COLORES_TEMA.map(normalizarHex).includes(normalizarHex(tema.color));
  const fondoCab = fondoCabecera(tema, API);

  return (
    <aside className="fe-panel-tema" aria-label="Personalizar tema" onMouseDown={(e) => e.stopPropagation()}>
      <header className="fe-panel-tema-cabeza">
        <Palette />
        <h2>Tema</h2>
        <button type="button" className="fe-icono-btn" onClick={onCerrar} aria-label="Cerrar tema" title="Cerrar">
          <X />
        </button>
      </header>
      <fieldset className="fe-panel-tema-cuerpo fm-scroll" disabled={deshabilitado}>
        <section>
          <h3>Estilo de texto</h3>
          {ESTILOS.map(([clave, nombre]) => {
            const f = tema.fuentes[clave];
            const rango = TAMANOS[clave];
            const tamanos = [];
            for (let n = rango.min; n <= rango.max; n += 1) tamanos.push(n);
            return (
              <div key={clave} className="fe-tema-fuente">
                <span className="fe-tema-fuente-nombre">{nombre}</span>
                <select
                  className="fe-select"
                  value={f.familia}
                  onChange={(e) => cambiarFuente(clave, { familia: e.target.value })}
                  aria-label={`Fuente del ${nombre.toLowerCase()}`}
                >
                  {FUENTES.map((familia) => <option key={familia} value={familia}>{familia}</option>)}
                </select>
                <select
                  className="fe-select fe-select-corto"
                  value={f.tamano}
                  onChange={(e) => cambiarFuente(clave, { tamano: Number(e.target.value) })}
                  aria-label={`Tamaño del ${nombre.toLowerCase()}`}
                >
                  {tamanos.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            );
          })}
        </section>

        <section>
          <h3>Encabezado</h3>
          {tema.imagen ? (
            <div className="fe-tema-cabecera-actual" style={{ background: fondoCab }}>
              <button type="button" className="fe-icono-btn fe-icono-flotante" onClick={() => cambiar({ imagen: null })} aria-label="Quitar imagen de encabezado" title="Quitar">
                <Trash2 />
              </button>
            </div>
          ) : null}
          <button type="button" className="fe-btn-borde fe-tema-subir" onClick={subirCabecera}>
            <ImagePlus /> Subir una imagen
          </button>
          <div className="fe-tema-presets" role="radiogroup" aria-label="Encabezados prediseñados">
            {Object.entries(PRESETS_CABECERA).map(([clave, p]) => {
              const elegido = tema.imagen?.preset === clave;
              return (
                <button
                  key={clave}
                  type="button"
                  role="radio"
                  aria-checked={elegido}
                  className={`fe-tema-preset ${elegido ? 'is-elegido' : ''}`}
                  style={{ background: p.css(tema.color) }}
                  onClick={() => cambiar({ imagen: { preset: clave } })}
                  title={p.nombre}
                  aria-label={p.nombre}
                >
                  {elegido && <Check />}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h3>Color</h3>
          <div className="fe-tema-colores" role="radiogroup" aria-label="Color del tema">
            {COLORES_TEMA.map((c) => {
              const elegido = normalizarHex(c) === normalizarHex(tema.color);
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={elegido}
                  className={`fe-tema-color ${elegido ? 'is-elegido' : ''}`}
                  style={{ background: c }}
                  onClick={() => cambiarColor(c)}
                  aria-label={`Color ${c}`}
                >
                  {elegido && <Check />}
                </button>
              );
            })}
            <label className={`fe-tema-color fe-tema-color-propio ${personalizado ? 'is-elegido' : ''}`} title="Color personalizado">
              <input
                type="color"
                value={tema.color}
                onChange={(e) => cambiarColor(e.target.value)}
                aria-label="Color personalizado"
              />
              <span style={{ background: personalizado ? tema.color : undefined }} />
            </label>
          </div>
        </section>

        <section>
          <h3>Color de fondo</h3>
          <div className="fe-tema-colores" role="radiogroup" aria-label="Color de fondo">
            {fondosDe(tema.color).map((c) => {
              const elegido = normalizarHex(c) === normalizarHex(tema.fondo);
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={elegido}
                  className={`fe-tema-color fe-tema-fondo ${elegido ? 'is-elegido' : ''}`}
                  style={{ background: c }}
                  onClick={() => cambiar({ fondo: c })}
                  aria-label={`Fondo ${c}`}
                >
                  {elegido && <Check />}
                </button>
              );
            })}
          </div>
        </section>
      </fieldset>
    </aside>
  );
}
